import { Property, Unit } from "types/landlord/address";
import { Template } from "types/landlord/verificationTemplate";
import {
  ApplicationStatus,
  DecisionAction,
  ApplicantType,
  Invitee,
  ScreeningStatus,
  RequiresOverridesIdentityVerification,
  ExecutionModes,
  VerificationNames
} from "shared/types";
import { Money } from "types/landlord/reports";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import {
  Applicant,
  CriteriaCheckSettings,
  MakeDecisionRequirements,
  Reasons
} from "types/application";

export type SlimApplicant = Pick<
  Applicant,
  | "id"
  | "role"
  | "first_name"
  | "last_name"
  | "middle_name"
  | "email"
  | "application_status"
  | "application_fee_status"
  | "canceled_by_auto_cancel"
>;

export interface ApplicationSummary {
  id: number;
  status: ApplicationStatus;
  screening_status: ScreeningStatus;
  conditions?: string[];
  unit_delisted?: boolean;
  auto_approved: boolean;
  started_at?: string;
  submitted_at?: string;
  first_submitted_at?: string;
  decision_at?: string;
  created_at?: string;
  updated_at?: string;
  desired_move_in_date?: string;
  desired_lease_term?: number;
  lease_term_enabled: boolean;
  monthly_rent_amount: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
  base_rent_amount?: Money;
  pet_rent_amount?: Money;
  housing_assistance_voucher_amount?: Money;
  latest_application_rent_history?: AdjustedApplicationRentActivity;
  include_co_signer_in_criteria_calculation?: boolean;
  eligible_for_role_update: boolean;
  total_applicants?: number;
  unit: Unit;
  property: Property;
  verification_template?: Template;
  applicants: SlimApplicant[];
  pending_magic_links?: Invitee[];
  assignees?: { id: number; name: string; email: string }[];
  score?: number;
  rule_reasons?: Reasons[];
  recommendation?: DecisionAction;
  lead_id?: number;
  partner_name_in_submission_disclosure?: string;
}

export interface ApplicationDecision {
  id: number;
  score?: number;
  rule_reasons?: Reasons[];
  auto_approved: boolean;
  recommendation?: DecisionAction;
  criteria_check_settings?: CriteriaCheckSettings;
  make_decision_requirements?: MakeDecisionRequirements[];
  requires_manual_or_conditional_execution?: {
    applicants?: {
      id: number;
      name: string;
      role: ApplicantType;
      verifications: {
        execution_mode: ExecutionModes;
        id: number;
        name: VerificationNames;
      }[];
    }[];
    total_verifications?: number;
  };
  requires_overrides_identity_verification?: RequiresOverridesIdentityVerification;
  voucher_amount?: Money;
  pets_count: number;
  group_stat: {
    average_credit_score?: number;
    min_credit_score?: number;
    max_credit_score?: number;
    no_credit_score_people?: number;
    self_reported_gross_income?: number;
    self_reported_gross_income_to_rent?: number;
    gross_income?: number;
    gross_income_to_rent_ratio?: number;
    net_income?: number;
    debt_to_net_income?: number;
  };
  conditional_approval_email_message?: string;
  approval_email_message?: string;
  declined_email_message?: string;
  has_pending_verifications: boolean;
}

export enum GroupStatMetricNames {
  GrossIncome = "verified_gross_income",
  NetIncome = "verified_net_income",
  GrossIncomeToRentRatio = "verified_gross_income_to_rent",
  NetIncomeToRentRatio = "verified_net_income_to_rent",
  DebtToGrossIncome = "debt_to_verified_gross_income",
  DebtToNetIncome = "debt_to_verified_net_income",
  DebtToSelfReportedGrossIncome = "debt_to_self_reported_gross_income",
  SelfReportedGrossIncome = "self_reported_gross_income",
  SelfReportedGrossIncomeToRent = "self_reported_gross_income_to_rent",
  AverageCreditScore = "average_credit_score",
  MinCreditScore = "lowest_credit_score",
  MaxCreditScore = "highest_credit_score",
  NoCreditScorePeople = "people_with_no_credit_score"
}

export type MetricSetting = "badge" | "role" | "name";

export type ApplicationMetricTemplateEntry = {
  metric: GroupStatMetricNames;
  index: number;
  title?: string;
  roles?: ApplicantType[];
  settings?: MetricSetting[];
};

export type ApplicantMetricContribution = {
  applicant_id: number;
  name: string;
  role: ApplicantType;
  value: number;
  debt?: number;
};

export type ApplicationMetricData = ApplicationMetricTemplateEntry & {
  value: number | null;
  badge: boolean;
  role_label: boolean;
  applicants: ApplicantMetricContribution[];
  warnings: {
    id: number; // applicantId
    name: string;
  }[];
  formula?: string;
  debt?: number;
};

export interface ApplicationMetricsResponse {
  metrics: ApplicationMetricData[];
  template: ApplicationMetricTemplateEntry[] | null;
}

export type ApplicantStepState =
  | "done"
  | "progress"
  | "processing"
  | "todo"
  | "manual"
  | "skipped"
  | "na";

export type ApplicantStepKey =
  | "rental_application"
  | "housing_history"
  | "identity_verification"
  | "income_verification"
  | "checkout";

export interface ApplicantProgress {
  id: number;
  last_activity_at: string | null;
  steps: Record<ApplicantStepKey, ApplicantStepState>;
}

export interface ApplicationProgress {
  application_id: number;
  primary_applicant_id: number | null;
  applicants: ApplicantProgress[];
}
